/**
 * useExpandedRowHover.js
 *
 * Row-level "expand on hover" for tables whose cells truncate with an ellipsis.
 *
 * Hovering any cell expands every cell in that row instead of just the hovered
 * one: the row has already grown to fit the hovered cell's wrapped value, so
 * wrapping its neighbours costs no extra height and saves hovering each long
 * column one at a time.
 *
 * Collapsing is deferred by a tick so that sliding from one cell to the next
 * within the same row (mouseleave then mouseenter) doesn't flash the row closed.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export default function useExpandedRowHover() {
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const collapseTimerRef = useRef(null);

  const cancelCollapse = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelCollapse, [cancelCollapse]);

  const getRowHoverProps = useCallback(
    (rowKey) => ({
      onMouseEnter: () => {
        cancelCollapse();
        setExpandedRowKey(rowKey);
      },
      onMouseLeave: () => {
        cancelCollapse();
        collapseTimerRef.current = setTimeout(() => setExpandedRowKey(null), 0);
      },
    }),
    [cancelCollapse]
  );

  return { expandedRowKey, getRowHoverProps };
}
